import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import translations from '../translations';
import GLBModel from './GLBModel';

const EntryDetail = ({ lang, favorites, toggleFavorite, loading, glbUrl, glbBotiAUrl, glbClaireUrl, glbRazeUrl }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const index = parseInt(id);
  const titleKey = `entry${index}_title`;
  const descKey = `entry${index}_desc`;
  const contentRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const imageRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const videos = {
    1: '/videos/fighter1.mp4',
    2: '/videos/fighterkick.mp4',
    3: '/videos/habitacion.mp4',
    4: '/videos/claire1.mp4',
    5: '/videos/fighter1.mp4',
    6: '/videos/fighter1.mp4',
    7: '/videos/fighter1.mp4',
    8: '/videos/fighter1.mp4',
    9: '/videos/fighter1.mp4',
    10: '/videos/fighter1.mp4',
    11: '/videos/fighter1.mp4',
    12: '/videos/fighter1.mp4',
    13: '/videos/fighter1.mp4',
    14: '/videos/fighter1.mp4',
    15: '/videos/fighter1.mp4',
    16: '/videos/fighter1.mp4',
    17: '/videos/fighter1.mp4',
    18: '/videos/fighter1.mp4',
  };

  const videoSets = {
    2: ['/videos/fighter1.mp4', '/videos/fighterkick.mp4', '/videos/habitacion.mp4', '/videos/fighterrun.mp4'],
    5: ['/videos/claire1.mp4', '/videos/claire2.mp4', '/videos/claire3.mp4', '/videos/claire4.mp4'],
    6: ['/videos/chasmcall.mp4'],
    9: ['/videos/raze1.mp4', '/videos/raze2.mp4', '/videos/raze3.mp4', '/videos/raze4.mp4'],
    10: ['/videos/mt yt.mp4'],
  };

  const imageSets = {
    7: ['/images/isla procedural.jpg', '/images/texturas-procedurales/cubodef.png', '/images/texturas-procedurales/nebulas1.png', '/images/texturas-procedurales/nebulas2.png'],
    166: ['/images/texturas-procedurales/pasto nodos blender.png', '/images/texturas-procedurales/landscape 2.png', '/images/texturas-procedurales/Isla landscape.png', '/images/texturas-procedurales/Oxide Bot04C.png'],
  };

  const title = translations[lang][titleKey] || `Entry ${index}`;
  const desc = translations[lang][descKey] || `Description ${index}`;
  const videoUrls = videoSets[index] || [videos[index] || '/videos/fighter1.mp4', '/videos/chasmcall.mp4', '/videos/habitacion.mp4', '/videos/fighter1.mp4'].filter(url => url);

  useEffect(() => {
    const smoothScrollTo = (targetY, duration) => {
      const startY = window.scrollY;
      const distance = targetY - startY;
      let startTime = null;

      const animation = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const ease = progress ** 2 * (3 - 2 * progress);
        window.scrollTo(0, startY + distance * ease);
        if (progress < 1) requestAnimationFrame(animation);
      };
      requestAnimationFrame(animation);
    };

    const scrollToContent = () => {
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        const targetY = window.scrollY + rect.top - (window.innerHeight - rect.height) / 2;
        smoothScrollTo(targetY, 1000);
      }
    };

    const timer = setTimeout(scrollToContent, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const closeImage = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedImage(null);
      setZoomLevel(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleZoom = (e) => {
    e.stopPropagation();
    if (!isDragging) {
      setZoomLevel(prev => {
        const newZoom = prev === 1 ? 2 : 1;
        return Math.min(Math.max(newZoom, 1), 2);
      });
    }
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      e.stopPropagation();
      e.preventDefault();
      setIsDragging(true);
      setStartPos({ x: e.clientX, y: e.clientY });
      if (imageRef.current) imageRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && imageRef.current) {
      const deltaX = e.clientX - startPos.x;
      const deltaY = e.clientY - startPos.y;
      const maxDrag = 50; // Límite de arrastre a 50 píxeles en cualquier dirección

      // Limitar el movimiento a ±50 píxeles
      const boundedX = Math.max(-maxDrag, Math.min(deltaX, maxDrag));
      const boundedY = Math.max(-maxDrag, Math.min(deltaY, maxDrag));

      setPosition({
        x: boundedX,
        y: boundedY,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (imageRef.current && zoomLevel > 1) imageRef.current.style.cursor = 'grab';
    else if (imageRef.current) imageRef.current.style.cursor = 'zoom-in';
  };

  return (
    <div ref={contentRef} className={`blog-entries mt-6 border border-white/10 rounded-xl p-8 shadow-[0_4px_12px_rgba(0,0,0,0.2)] bg-[#121212] min-h-screen ${index === 1 || index === 3 || index === 4 ? 'single-column' : ''} animate-entry`}>
      <h2 className="text-[#5A4C70] font-['Inter'] font-light text-[1em] tracking-[0.375em] mb-3">
        {title}
      </h2>
      <p className="text-[#A0A0A0] font-['Inter'] text-[0.75em] leading-[1.40625] font-light mb-6">
        {desc}
      </p>

      {index === 1 && (
        <div className="flex flex-col justify-center items-center h-full">
          <iframe frameborder="0" src="https://itch.io/embed/3800514?bg_color=222222&amp;fg_color=eeeeee&amp;link_color=6d1abc&amp;border_color=363636" width="552" height="167">
            <a href="https://kuni3d.itch.io/pancakes-bunny">Pancake Bunny by kuni3d, ZoeMuruchi, Michiellamorte</a>
          </iframe>
          <button
            className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mt-6 mx-auto"
            onClick={() => navigate('/')}
          >
            Back
          </button>
        </div>
      )}

      {index === 2 && (
        <div className="grid grid-cols-2 gap-4 mb-6 entry-2-videos">
          {videoUrls.map((url, i) => (
            <video
              key={i}
              width="100%"
              height="200"
              src={url}
              autoPlay
              loop
              controls
              className="rounded-md"
            >
              Internet?
            </video>
          ))}
        </div>
      )}
      {index === 2 && <div className="glb-container"><GLBModel url={glbUrl} /></div>}
      {index === 2 && (
        <button
          className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mx-auto block mt-6"
          onClick={() => navigate('/')}
        >
          Back
        </button>
      )}

      {index === 3 && (
        <div className="flex flex-col justify-center items-center h-full">
          <iframe
            src="https://kuni3d.github.io/devlog/game.html"
            title="Catito Game"
            className="w-[800px] h-[600px] rounded-md"
            allowFullScreen
          />
          <button
            className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mt-6 mx-auto"
            onClick={() => window.location.href = 'https://kuni3d.github.io/devlog/game.html'}
          >
            Juego Completo
          </button>
          <button
            className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mt-6 mx-auto"
            onClick={() => navigate('/')}
          >
            Back
          </button>
        </div>
      )}

      {index === 4 && (
        <div className="flex flex-col justify-center items-center h-full">
          <iframe
            src="https://kuni3d.github.io/goosefootball/"
            title="Goose Football Game"
            className="w-[800px] h-[600px] rounded-md"
            allowFullScreen
          />
          <button
            className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mt-6 mx-auto"
            onClick={() => window.location.href = 'https://kuni3d.github.io/goosefootball/'}
          >
            Juego Completo
          </button>
          <button
            className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mt-6 mx-auto"
            onClick={() => navigate('/')}
          >
            Back
          </button>
        </div>
      )}

      {index === 5 && (
        <div className="grid grid-cols-2 gap-4 mb-6 entry-2-videos">
          {videoUrls.map((url, i) => (
            <video
              key={i}
              width="100%"
              height="200"
              src={url}
              loop
              controls
              className="rounded-md"
            >
              Internet?
            </video>
          ))}
        </div>
      )}
      {index === 5 && <div className="glb-container"><GLBModel url={glbClaireUrl} /></div>}
      {index === 5 && (
        <p className="text-[#A0A0A0] font-['Inter'] text-[0.75em] leading-[1.40625] font-light mb-6">
          {translations[lang].claireanim01}
        </p>
      )}
      {index === 5 && (
        <p className="text-[#A0A0A0] font-['Inter'] text-[0.75em] leading-[1.40625] font-light mb-6">
          {translations[lang].claireanim02}
        </p>
      )}
      {index === 5 && (
        <button
          className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mx-auto block mt-6"
          onClick={() => navigate('/')}
        >
          Back
        </button>
      )}

      {index === 6 && (
        <div className="grid grid-cols-2 gap-4 mb-6 entry-2-videos">
          {videoSets[6].map((url, i) => (
            <video
              key={i}
              width="100%"
              height="200"
              src={url}
              loop
              controls
              className="rounded-md"
            >
              Internet?
            </video>
          ))}
        </div>
      )}
      {index === 6 && <div className="glb-container"><GLBModel url={glbBotiAUrl} /></div>}
      {index === 6 && (
        <p className="text-[#A0A0A0] font-['Inter'] text-[0.75em] leading-[1.40625] font-light mb-6">
          {translations[lang].bot_desc01.split('\n').map((line, i) => (
            <span key={i}>
              {line}
              <br />
            </span>
          ))}
        </p>
      )}
      {index === 6 && (
        <p className="text-[#A0A0A0] font-['Inter'] text-[0.75em] leading-[1.40625] font-light mb-6">
          {translations[lang].bot_desc02}
        </p>
      )}
      {index === 6 && (
        <button
          className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mx-auto block mt-6"
          onClick={() => navigate('/')}
        >
          Back
        </button>
      )}

      {index === 7 && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {imageSets[166].map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Imagen ${i + 1} de la entrada ${index}`}
              width="100%"
              height="200"
              className="rounded-md object-cover hover:scale-105 hover:shadow-lg transition-transform duration-300 cursor-pointer"
              onClick={() => handleImageClick(url)}
            />
          ))}
        </div>
      )}
      {index === 7 && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {imageSets[7].map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Imagen ${i + 1} de la entrada ${index}`}
              width="100%"
              height="200"
              className="rounded-md object-cover hover:scale-105 hover:shadow-lg transition-transform duration-300 cursor-pointer"
              onClick={() => handleImageClick(url)}
            />
          ))}
        </div>
      )}
      {index === 7 && (
        <button
          className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mx-auto block mt-6"
          onClick={() => navigate('/')}
        >
          Back
        </button>
      )}

      {index === 8 && (
        <button
          className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mx-auto block mt-6"
          onClick={() => navigate('/')}
        >
          Back
        </button>
      )}

      {index === 9 && (
        <div className="grid grid-cols-2 gap-4 mb-6 entry-2-videos">
          {videoSets[9].map((url, i) => (
            <video
              key={i}
              width="100%"
              height="200"
              src={url}
              loop
              controls
              className="rounded-md"
            >
              Internet?
            </video>
          ))}
        </div>
      )}

      {index === 9 && <div className="glb-container"><GLBModel url={glbRazeUrl} /></div>}

      {index === 9 && (
        <button
          className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mx-auto block mt-6"
          onClick={() => navigate('/')}
        >
          Back
        </button>
      )}

      {index === 10 && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {videoSets[10].map((url, i) => (
            <video
              key={i}
              width="100%"
              height="200"
              src={url}
              loop
              autoPlay
              controls
              className="rounded-md"
            >
              Internet?
            </video>
          ))}
        </div>
      )}

      {index === 10 && (
        <button
          className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mx-auto block mt-6"
          onClick={() => navigate('/')}
        >
          Back
        </button>
      )}
      {index === 18 && (
        <button
          className="bg-gradient-to-br from-[#4B0082] to-[#6B46C1] border border-[#4B0082] rounded-full px-3 py-1 text-xs text-[#D1C4E9] hover:bg-gradient-to-br hover:from-[#5C2E91] hover:to-[#7A5DA5] hover:text-white transition-all mx-auto block mt-6"
          onClick={() => navigate('/')}
        >
          Back
        </button>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={closeImage}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <img
            ref={imageRef}
            src={selectedImage}
            alt="Imagen ampliada"
            className="max-w-[100hv] max-h-[35vh] object-contain"
            style={{
              transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
              transformOrigin: 'center',
              cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
            }}
            onClick={handleZoom}
            onMouseEnter={e => {
              if (zoomLevel > 1) e.target.style.cursor = 'grab';
              else e.target.style.cursor = 'zoom-in';
            }}
            onMouseLeave={e => {
              if (!isDragging) e.target.style.cursor = 'default';
            }}
            onMouseDown={handleMouseDown}
          />
        </div>
      )}

      <button
        className="absolute top-2 right-2 text-[#5A4C70] hover:text-[#8E8E8E] transition-colors text-lg"
        onClick={() => toggleFavorite(index)}
        disabled={loading}
      >
        {favorites.includes(index) ? '★' : '☆'}
      </button>
    </div>
  );
};

export default EntryDetail;
